"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

const steps = [
  {
    number: "01",
    title: "Login",
    description: "Register and log into our easy to use app on any device.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    color: "from-brand-purple to-accent-purple",
    bgColor: "bg-light-purple",
    textColor: "text-brand-purple",
  },
  {
    number: "02",
    title: "Plan Your Trip",
    description: "Using geolocation and auto completion set your Pickup and Drop-off locations.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    color: "from-brand-green to-green-600",
    bgColor: "bg-green-50",
    textColor: "text-brand-green",
  },
  {
    number: "03",
    title: "Pick Your Truck",
    description: "Select from our fleet of available trucks so we can GoGedit.",
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <rect x="1" y="3" width="15" height="13" rx="2" />
        <path d="M16 8h4l3 5v3h-7V8z" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
    color: "from-dark-navy to-deep-navy",
    bgColor: "bg-light-blue",
    textColor: "text-dark-navy",
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll(".step-item");
    if (!items) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    items?.forEach((item) => observer?.observe(item));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-28 bg-white relative overflow-hidden">
      {/* Background grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#4732D7 1px, transparent 1px), linear-gradient(90deg, #4732D7 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="section-label mx-auto w-fit mb-6">How It Works</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-deep-navy leading-tight mb-4">
            How does it work?
          </h2>
          <p className="text-body-text text-lg max-w-xl mx-auto">
            Getting your items from A to B has never been easier. Three simple steps.
          </p>
        </div>

        {/* Steps */}
        <div ref={sectionRef} className="grid md:grid-cols-3 gap-8 relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-16 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-0.5 bg-gradient-to-r from-brand-purple via-brand-green to-dark-navy opacity-20 z-0" />

          {steps?.map((step, idx) => (
            <div
              key={step?.number}
              className={`step-item on-scroll stagger-${idx + 1} relative z-10`}
            >
              <div className="bg-white rounded-4xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 group border border-border-gray hover:shadow-2xl hover:border-brand-purple/30 hover:bg-gradient-to-br hover:from-white hover:to-gray-50">
                {/* Number badge */}
                <div className="flex items-start justify-between mb-6">
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step?.color} flex items-center justify-center text-white shadow-purple-sm`}
                  >
                    {step?.icon}
                  </div>
                  <span className="font-heading font-800 text-5xl text-border-gray group-hover:text-brand-purple/20 transition-colors">
                    {step?.number}
                  </span>
                </div>

                <h3 className="font-heading text-xl font-bold text-deep-navy mb-3">{step?.title}</h3>
                <p className="text-body-text leading-relaxed">{step?.description}</p>

                {/* Progress indicator */}
                <div className={`mt-6 h-1 rounded-full bg-gradient-to-r ${step?.color} opacity-30 group-hover:opacity-100 transition-opacity`} />
              </div>

              {/* Arrow between steps */}
              {idx < steps?.length - 1 && (
                <div className="hidden md:flex absolute top-16 -right-4 z-20 w-8 h-8 bg-white rounded-full shadow-card items-center justify-center">
                  <svg width="14" height="14" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-body-text mb-6">
            Exclusive platform to help you{" "}
            <strong className="text-deep-navy">move your stuff.</strong>{" "}
            Spend less time worrying about how you are moving large items from one place to another.
            Spend more time getting stuff moving.
          </p>
          <Link href="/be-a-user" className="btn-primary">
            Download the App
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}